import { TableActionGroup, TableActionButton } from '@channelplay/table-actions';
import MoreFiltersDropdown from '../components/MoreFiltersDropdown';
import { useEffect, useLayoutEffect, useState, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Download, Upload, Info } from 'lucide-react';
import Modal from '../components/Modal';
import EmployeeDetailsModal from '../components/EmployeeDetailsModal';
import StoreCustomFieldInputs from '../components/StoreCustomFieldInputs';
import EmployeeCustomFieldsBulkUploadModal from '../components/EmployeeCustomFieldsBulkUploadModal';
import {
  employeesAPI, storesAPI, externalUsersAPI, employeeCustomFieldsAPI,
  type EmployeeCustomField, type EmployeeCustomFieldType,
} from '../services/api';
import { buildCsv, downloadCsv } from '../utils/csv';
import { formatIsoDay } from '../utils/dateHelpers';
import { useAuthStore } from '../stores/authStore';
import { EmployeeTableSkeleton } from '../components/Skeleton';
import { InlineLoader } from '../components/loading';
import { useTableSort, SortTh, toEpochMs, type SortAccessors, type SortDir } from '../components/dashboard/useTableSort';
import MasterTabStrip from '../components/MasterTabStrip';
import { rosterTabs, type RosterCounts, type RosterTab } from '../utils/employeeRosterTabs';
import { useDeployHiringCheck } from '../components/hiring/useDeployHiringCheck';
import { hiringUserKeyOf } from '../components/hiring/useHiringAccess';

interface Employee {
  emp_code?: string | number;
  emp_name?: string;
  employeeCode?: string | number;
  employeeName?: string;
  name?: string;
  fullName?: string;
  employeeFullName?: string;
  empName?: string;
  employee_name?: string;
  userName?: string;
  email?: string;
  contactNumber?: string;
  contact_number?: string;
  designation?: string;
  /** From employee_personal_details, attached by both roster endpoints; absent when not recorded. */
  gender?: string;
  reportingManager?: string;
  reporting_manager?: string;
  status?: string;
  active?: boolean;
  [key: string]: any; // Allow any additional fields from database
}

type StoreOption = {
  id: string;
  storeCode: string;
  name: string;
  pinCode: string;
  city: string;
  address: string;
  category?: string;
  categoryId?: number;
  state?: string;
  trade?: string;
};

/** One store an employee is mapped to (GET /stores/employee/:code/assignments). */
type StoreAssignment = {
  id: string;
  store_name: string;
  store_code?: string | null;
  city?: string | null;
  state?: string | null;
  store_status?: string | null;
  is_primary?: boolean;
};

/**
 * The employee's city, as one definition shared by the City column, the CSV, the
 * City filter and its dropdown options.
 *
 * It lives here rather than being spelled out at each call site because the four
 * drifting apart is exactly what broke the filter: the column read `cityName`
 * while the filter compared `location`, which on this endpoint is the full job
 * ADDRESS string (mapExternalEmployee sets it from `jobAddress`), never a city.
 * Nothing ever matched, so picking any city emptied the table.
 *
 * `location` stays last in the precedence — same order the column already used —
 * so a row with no city at all still filters by whatever the column shows it as,
 * rather than silently dropping out of every selection.
 */
const cityOf = (emp: Employee): string => {
  for (const key of ['cityName', 'city', 'location']) {
    const value = emp[key];
    if (value !== undefined && value !== null && value !== '') return String(value).trim();
  }
  return '';
};

// getName and pickField read nothing but their arguments. They sit at module scope so
// the column-sort accessors below resolve each cell with the SAME helpers the table
// and the CSV use — one definition of what a column shows.
const getName = (emp: Employee) =>
  emp.emp_name ||
  emp.employeeName ||
  emp.employeeFullName ||
  emp.empName ||
  emp.employee_name ||
  emp.name ||
  emp.fullName ||
  emp.userName ||
  'Unnamed employee';

const pickField = (emp: Employee, keys: string[], fallback: string | number = '—') => {
  for (const key of keys) {
    const value = emp[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return fallback;
};

/**
 * Gender, as the Gender column, its sort and the CSV read it: '' when none is recorded.
 * Attached by the server from ChannelPlex's employee_personal_details (synced from
 * 1Office), already in one capitalisation.
 */
const genderOf = (emp: Employee): string => String(emp.gender ?? '').trim();

/**
 * The Designation column: the employee's BUDGET name — on iPro "iPro" or "Senior iPro" —
 * the same value as the All Budgets filter and the CSV's Budget Name column. It is not
 * the 1Office HR `designation` field, which reads "In-store Sales Promoter for Apple" for
 * nearly everyone (see the CSV below). '' when there is no budget.
 */
const designationOf = (emp: Employee): string =>
  String(pickField(emp, ['budgetName', 'department', 'budget_name'], '')).trim();

/** An employee's code as the search box reads it: lower case, '' when they have none. */
const searchCodeOf = (emp: Employee): string =>
  (emp.emp_code || emp.employeeCode || emp.userName)?.toString().toLowerCase() || '';

/** The custom-field types the search box looks in. Yes/No fields are left out: typing
 *  "yes" would find half the roster. */
const SEARCHED_FIELD_TYPES = new Set<EmployeeCustomFieldType>(['text', 'number', 'date', 'dropdown', 'multiple_choice']);

/** No custom fields to search: a stable value, so the search callbacks below keep their identity. */
const NO_SEARCHED_FIELDS: EmployeeCustomField[] = [];

/** What found an employee: their name or code, or one custom field's value (shown under the name). */
type SearchHit = { by: 'name-or-code' } | { by: 'field'; label: string; value: string };
const NAME_OR_CODE_HIT: SearchHit = { by: 'name-or-code' };

/**
 * How the search box's text (trimmed, lower case) finds this employee, or null when it
 * does not. Name and code are checked first; then each of `fields`, in the order the
 * page lists them, against the value as stored ("contains", the first field that matches
 * wins). A multiple-choice value is checked one picked option at a time, and the hit names
 * only the options that matched.
 *
 * Only the fields passed in are read, so a value left under a field that is no longer
 * active, or not in the page's definitions at all, never matches.
 */
const searchHitOf = (emp: Employee, q: string, fields: EmployeeCustomField[]): SearchHit | null => {
  if (!q || getName(emp).toLowerCase().includes(q) || searchCodeOf(emp).includes(q)) return NAME_OR_CODE_HIT;
  const values = emp.custom_fields as Record<string, unknown> | undefined;
  if (!values) return null;
  for (const field of fields) {
    if (field.active === false || !SEARCHED_FIELD_TYPES.has(field.fieldType)) continue;
    const raw = values[field.id];
    // A number field's value is a JSON number (iPro's DS Person ID is one); String() gives
    // back its plain digits.
    const matched = (Array.isArray(raw) ? raw : [raw])
      .filter((v): v is string | number => typeof v === 'string' || typeof v === 'number')
      .map(String)
      .filter((v) => v.toLowerCase().includes(q));
    if (matched.length > 0) return { by: 'field', label: field.label, value: matched.join(', ') };
  }
  return null;
};

/** Roster columns that sort newest / longest-first on their first click; every other
 *  column is text and starts A→Z. */
const EMPLOYEE_DESC_FIRST_SORT_KEYS = new Set(['joinedDate', 'exitDate', 'ageOnNetwork']);
const employeeFirstSortDir = (key: string): SortDir => (EMPLOYEE_DESC_FIRST_SORT_KEYS.has(key) ? 'desc' : 'asc');

/**
 * How each roster column sorts, keyed by the `sortKey` on its header. Every accessor
 * resolves its value exactly as the cell does (same field precedence), minus the '—'
 * placeholder, so blanks sink to the bottom and the order matches what is on screen.
 *
 * Dates sort on the real timestamp. Age on Network sorts on elapsed time rather than
 * the "1y 2m" label: to today on the active tabs, to the exit date on the Inactive tab
 * — the same span the cell shows. `now` is fixed once per map so every row is measured
 * to the same instant.
 *
 * The map holds ONLY the columns the tab on screen has. The rows of every tab carry
 * the other tab's fields too (a leaver still has an official email), so a full map
 * would let a sort picked on Deployed reorder the Inactive table by a column it does
 * not show, with no header saying so.
 */
const buildEmployeeSortAccessors = (inactiveTab: boolean): SortAccessors<Employee> => {
  const now = Date.now();
  const text = (emp: Employee, keys: string[]) => String(pickField(emp, keys, ''));
  const shared: SortAccessors<Employee> = {
    code: (e) => text(e, ['emp_code', 'employeeCode', 'userName']),
    name: (e) => getName(e),
    gender: (e) => genderOf(e),
    mobile: (e) => text(e, ['contact_number', 'contactNo', 'mobile', 'mobile_number', 'phone']),
    role: (e) => e.roleName,
    designation: (e) => designationOf(e),
    reportingHead: (e) => e.reportingToName || e.reportingManager || e.reporting_manager,
    managerCode: (e) => e.reportingToEmpCode,
    joinedDate: (e) => toEpochMs(pickField(e, ['dateOfJoining', 'joinedDate', 'joiningDate', 'joined_on', 'date_of_joining'], '')),
    ageOnNetwork: (e) => {
      const joined = toEpochMs(e.dateOfJoining || e.joinedDate || e.date_of_joining);
      const until = inactiveTab && e.exitDate ? toEpochMs(e.exitDate) : now;
      return joined === null || until === null ? null : until - joined;
    },
    state: (e) => text(e, ['stateName', 'state']),
    city: (e) => cityOf(e),
  };
  return inactiveTab
    ? { ...shared, exitDate: (e) => toEpochMs(e.exitDate), lastStore: (e) => e.lastStoreName }
    : {
        ...shared,
        clientReportingManager: (e) => e.clientReportingManagers,
        officialEmail: (e) => text(e, ['officialEmail', 'email', 'personalEmail']),
        pincode: (e) => text(e, ['jobPincode', 'pincode', 'pin']),
      };
};

/** What the Inactive tab's request says about the list it returned. */
interface InactiveMeta {
  months: number;
  /** First day of the window, 'YYYY-MM-DD'. */
  since: string;
  today: string;
  total: number;
  /** Inactive employees with no exit date on record — not placeable, so not listed. */
  undatedCount: number;
  /** Everything 1Office returned as inactive for the project. */
  inactiveRows: number;
}

/** Which roster column decided an inactive employee's exit date. */
const EXIT_DATE_SOURCE_LABEL: Record<string, string> = {
  inactive_date: 'Inactive date',
  last_working_day: 'Last working day',
  resignation_date: 'Resignation date',
};

/** One custom-field value as a CSV cell — Yes/No for booleans, pipe-joined for
 * multi-selects, everything else as-is. Matches the store CSV export. */
const formatCustomFieldForCsv = (field: EmployeeCustomField, value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';
  if (field.fieldType === 'boolean') return value === true ? 'Yes' : value === false ? 'No' : '';
  if (Array.isArray(value)) return value.map((v) => String(v)).join(' | ');
  return String(value);
};

export default function EmployeesPage({
  embedded, actionSlot, onCount, initialSearch = '', rosterTab, onRosterTabChange, onTabCounts,
}: {
  embedded?: boolean;
  actionSlot?: HTMLElement | null;
  onCount?: (n: number) => void;
  /** What the search box starts with: the ?q= of a link to this page, read by EmployeeMasterPage. */
  initialSearch?: string;
  /**
   * The open list, when the page around this one draws the tabs (EmployeeMasterPage puts
   * them in its one row, beside Custom Fields). Given, this page draws no tab strip of its
   * own and asks `onRosterTabChange` to move to another list. Left out, it keeps the open
   * list itself and draws its own strip.
   */
  rosterTab?: RosterTab;
  onRosterTabChange?: (tab: RosterTab) => void;
  /** Each list's count as its tab shows it, for the page that draws the tabs. */
  onTabCounts?: (counts: RosterCounts) => void;
} = {}) {
  /**
   * Both halves of the active roster. One fetch returns both lists (deployed +
   * undeployed), and both are kept — the tab badges have to respond to filters too,
   * and a count can only be filtered if its rows are in hand. It also means switching
   * between the two tabs is a view change, not a refetch.
   */
  const [deployedEmployees, setDeployedEmployees] = useState<Employee[]>([]);
  const [undeployedEmployees, setUndeployedEmployees] = useState<Employee[]>([]);
  // Unfiltered server totals. Still the source for the badges when NO filter is on, so
  // the default view keeps showing the authoritative count rather than a page-local length.
  const [deployedCount, setDeployedCount] = useState(0);
  const [undeployedCount, setUndeployedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /**
   * The Inactive tab: people 1Office has marked inactive in the last three months. Its
   * own request (it reads 1Office's inactive roster, which nothing else on the page
   * needs), made the first time the tab is opened for a project — not on every visit.
   */
  const [inactiveEmployees, setInactiveEmployees] = useState<Employee[]>([]);
  const [inactiveMeta, setInactiveMeta] = useState<InactiveMeta | null>(null);
  const [inactiveLoading, setInactiveLoading] = useState(false);
  const [inactiveError, setInactiveError] = useState<string | null>(null);
  /** The project the inactive list was loaded for; a switch discards it. */
  const [inactiveLoadedFor, setInactiveLoadedFor] = useState<number | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [ownTab, setOwnTab] = useState<RosterTab>('deployed');
  const drawsOwnTabs = rosterTab === undefined;
  const activeTab: RosterTab = rosterTab ?? ownTab;
  const setActiveTab = useCallback(
    (tab: RosterTab) => {
      if (drawsOwnTabs) setOwnTab(tab);
      onRosterTabChange?.(tab);
    },
    [drawsOwnTabs, onRosterTabChange],
  );
  const [filters, setFilters] = useState({
    budget: '',
    projectName: '',
    state: '',
    city: '',
    tenure: '',
    reportingHead: '',
  });
  // Starts on what a link asked for, so a row on the Data Hygiene dashboard opens on its
  // person. Only the start: the box is the person's from then on, and a later change of
  // the prop is not read.
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  // Employee custom fields: the project's active definitions, plus the values for
  // the employee currently open in the edit modal. Seed Score is now one of these.
  const [customFieldDefs, setCustomFieldDefs] = useState<EmployeeCustomField[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, unknown>>({});
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [deployModalEmployee, setDeployModalEmployee] = useState<Employee | null>(null);
  const [detailsModalUserId, setDetailsModalUserId] = useState<number | string | null>(null);
  const [storeSearch, setStoreSearch] = useState('');
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [availableStores, setAvailableStores] = useState<StoreOption[]>([]);
  const [_isLoadingStores, setIsLoadingStores] = useState(false);
  const [currentDeployment, setCurrentDeployment] = useState<any>(null);
  // Multi-store deployment (roaming promoters). Loaded alongside currentDeployment when the
  // deploy modal opens: when the employee's role allows several stores, the modal switches
  // from "move to a store" to "add a store" and lists every store they're mapped to.
  const [storeAssignments, setStoreAssignments] = useState<StoreAssignment[]>([]);
  const [multiStoreAllowed, setMultiStoreAllowed] = useState(false);
  const [assignmentBusy, setAssignmentBusy] = useState(false);

  // Reporting hierarchy state for edit modal
  const [selectedReportees, setSelectedReportees] = useState<string[]>([]);
  const [selectedManagers, setSelectedManagers] = useState<string[]>([]);
  const [loadingReporting, setLoadingReporting] = useState(false);
  const [externalUsers, setExternalUsers] = useState<{ employee_code: string; name: string; mobile: string }[]>([]);

  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);
  const hiringUserKey = useAuthStore((state) => hiringUserKeyOf(state.user));

  // "Employees (N)" in the tab shell is reported further down — it follows the badge
  // counts, so it has to be declared after them (see deployedBadge/undeployedBadge).

  // Load the project's active custom-field definitions for the edit modal + CSV.
  useEffect(() => {
    if (!selectedProjectId) { setCustomFieldDefs([]); return; }
    let cancelled = false;
    employeeCustomFieldsAPI.list(selectedProjectId, false)
      .then((res) => { if (!cancelled) setCustomFieldDefs(res.data?.data || []); })
      .catch(() => { if (!cancelled) setCustomFieldDefs([]); });
    return () => { cancelled = true; };
  }, [selectedProjectId]);

  // Fetch all employees from external API and categorize by deployed status
  const fetchEmployees = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Use the new project-employees endpoint that fetches from external API
      // and categorizes by checking employee codes in stores table.
      // Only active employees — the backend already passes includeNatsNaps and
      // includeContractual so all active employee categories come through.
      const response = await employeesAPI.getProjectEmployees(true, selectedProjectId ?? undefined);
      const responseData = response.data?.data || response.data || {};
      const meta = response.data?.meta || {};
      
      // Get deployed and undeployed arrays from response
      const deployed = responseData.deployed || [];
      const undeployed = responseData.undeployed || [];

      // Set counts from API meta
      setDeployedCount(meta.deployedCount || deployed.length);
      setUndeployedCount(meta.undeployedCount || undeployed.length);

      // Both halves are kept, so the tab not on screen can still be filtered for its
      // badge rather than frozen at the project total.
      setDeployedEmployees(deployed);
      setUndeployedEmployees(undeployed);
    } catch (err) {
      console.error('Failed to fetch employees', err);
      setError('Failed to load employees. Please try again.');
      setDeployedEmployees([]);
      setUndeployedEmployees([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch on mount and when the project changes. Both tabs come from this one response.
  useEffect(() => {
    fetchEmployees();
  }, [selectedProjectId]);

  // When a deploy is pressed, hiring at the store joined is asked about BEFORE it is saved, and
  // the store left by a move once it is (product owner, 21 and 22 Oct 2026). This page shows no
  // hiring itself: only a hiring closed as filled from those windows changes it (someone was
  // placed), and the people are read again.
  const deployHiring = useDeployHiringCheck({
    projectId: selectedProjectId,
    userKey: hiringUserKey,
    onPlaced: () => {
      void fetchEmployees();
    },
  });


  // The project the page is showing now, so a slow inactive-roster response for the
  // PREVIOUS project cannot land on this one.
  const currentProjectRef = useRef(selectedProjectId);
  currentProjectRef.current = selectedProjectId;

  const fetchInactiveEmployees = async () => {
    const projectId = selectedProjectId;
    if (!projectId) return;
    setInactiveLoading(true);
    setInactiveError(null);
    try {
      const response = await employeesAPI.getRecentlyInactiveEmployees(projectId);
      if (currentProjectRef.current !== projectId) return;
      setInactiveEmployees(response.data?.data?.employees || []);
      setInactiveMeta(response.data?.meta || null);
      setInactiveLoadedFor(projectId);
    } catch (err) {
      if (currentProjectRef.current !== projectId) return;
      console.error('Failed to fetch inactive employees', err);
      setInactiveError('Failed to load inactive employees. Please try again.');
      setInactiveEmployees([]);
      setInactiveMeta(null);
    } finally {
      if (currentProjectRef.current === projectId) setInactiveLoading(false);
    }
  };

  // A different project's leavers are not this one's. Loading is reset too: a request
  // still in flight for the previous project is ignored when it lands, so nothing else
  // would clear it — and a stuck flag would stop this project's list from ever loading.
  useEffect(() => {
    setInactiveEmployees([]);
    setInactiveMeta(null);
    setInactiveError(null);
    setInactiveLoadedFor(null);
    setInactiveLoading(false);
  }, [selectedProjectId]);

  // Loaded the first time the Inactive tab is opened for a project.
  useEffect(() => {
    if (activeTab !== 'inactive' || !selectedProjectId) return;
    if (inactiveLoadedFor === selectedProjectId || inactiveLoading || inactiveError) return;
    void fetchInactiveEmployees();
  }, [activeTab, selectedProjectId, inactiveLoadedFor, inactiveLoading, inactiveError]);

  const isInactiveTab = activeTab === 'inactive';

  // The rows the tab on screen lists — what the table, the filters' options and the
  // CSV all read.
  const employees = activeTab === 'deployed'
    ? deployedEmployees
    : activeTab === 'undeployed'
      ? undeployedEmployees
      : inactiveEmployees;

  // Reflect a change to one employee on whichever list holds them.
  const updateEmployeeEverywhere = (code: string, patch: (emp: Employee) => Employee) => {
    const apply = (list: Employee[]) =>
      list.map((emp) => (getEmployeeCode(emp) === code ? patch(emp) : emp));
    setDeployedEmployees(apply);
    setUndeployedEmployees(apply);
    setInactiveEmployees(apply);
  };

  // After a bulk upload of custom-field values, every list that is on hand is stale.
  const refreshAfterBulkUpload = () => {
    fetchEmployees();
    if (inactiveLoadedFor === selectedProjectId) void fetchInactiveEmployees();
  };

  const getEmployeeCode = (emp?: Employee | null) => {
    if (!emp) return '';
    return (emp.emp_code || emp.employeeCode || emp.userName || '').toString();
  };

  const LEVEL_BELOW: Record<string, string> = { L2: 'L1', L3: 'L2', L4: 'L3', L5: 'L4' };

  const handleCustomFieldChange = (fieldId: string, value: unknown) => {
    setCustomFieldValues((prev) => ({ ...prev, [fieldId]: value }));
    setSaveError('');
  };

  const handleEdit = async (employee: Employee) => {
    setSelectedEmployee(employee);
    // Values ride along on the list payload as `custom_fields` ({ fieldId: value }).
    setCustomFieldValues({ ...((employee.custom_fields as Record<string, unknown>) || {}) });
    setSaveError('');
    setSelectedReportees([]);
    setSelectedManagers([]);
    setIsEditModalOpen(true);

    const empCode = getEmployeeCode(employee);
    const category = employee.roleCategory;
    if (!empCode || !selectedProjectId) return;
    // Somebody who has left has no reporting line to edit — the modal shows only their
    // custom fields, so there is nothing to load.
    if (employee.isActiveEmployee === false) return;

    setLoadingReporting(true);
    try {
      // Fetch external users for Client Reporting Manager dropdown
      const extRes = await externalUsersAPI.getByProject(selectedProjectId);
      setExternalUsers(extRes.data?.data || []);

      // Fetch current managers (an employee may report to multiple external managers)
      const res = await employeesAPI.getManager(empCode, selectedProjectId);
      const codes: string[] = Array.isArray(res.data?.data?.managerCodes)
        ? res.data.data.managerCodes
        : res.data?.data?.managerCode
          ? [res.data.data.managerCode]
          : [];
      setSelectedManagers(codes);

      if (category && LEVEL_BELOW[category]) {
        // Fetch current reportees
        const reporteesRes = await employeesAPI.getReportees(empCode, selectedProjectId);
        setSelectedReportees(reporteesRes.data?.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch reporting data:', err);
    } finally {
      setLoadingReporting(false);
    }
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedEmployee(null);
    setCustomFieldValues({});
    setSaveError('');
    setSelectedReportees([]);
    setSelectedManagers([]);
  };

  const fetchAvailableStores = async () => {
    setIsLoadingStores(true);
    try {
      const response = await storesAPI.getAvailableStores({ projectId: selectedProjectId ?? undefined });
      const stores = response.data?.data || response.data || [];
      // Deduplicate stores by id using a Map
      const storeMap = new Map<string, StoreOption>();
      stores.forEach((store: any) => {
        // Skip if we already have this store
        if (storeMap.has(store.id)) return;
        
        // Handle category which can be an object or string
        let categoryName = '';
        if (store.category_name) {
          categoryName = store.category_name;
        } else if (store.category && typeof store.category === 'object') {
          categoryName = store.category.name || store.category.category_name || '';
        } else if (typeof store.category === 'string') {
          categoryName = store.category;
        }
        
        // Get category ID
        let categoryId: number | undefined;
        if (store.store_category) {
          categoryId = store.store_category;
        } else if (store.category && typeof store.category === 'object') {
          categoryId = store.category.id;
        }
        
        storeMap.set(store.id, {
          id: store.id,
          storeCode: store.store_code || store.storeCode || '',
          name: store.store_name || store.name || '',
          pinCode: store.pin_code || store.pinCode || '',
          city: store.city || '',
          state: store.state || '',
          address: store.address || '',
          category: categoryName,
          categoryId,
          trade: store.trade || '',
        });
      });
      setAvailableStores(Array.from(storeMap.values()));
    } catch (err) {
      console.error('Failed to fetch stores:', err);
      setAvailableStores([]);
    } finally {
      setIsLoadingStores(false);
    }
  };

  const fetchCurrentDeployment = async (empCode: string) => {
    try {
      const response = await storesAPI.getByEmployee(empCode);
      const storeData = response.data?.data || response.data;
      // API returns an array, get the first store
      const store = Array.isArray(storeData) ? storeData[0] : storeData;
      setCurrentDeployment(store || null);
    } catch (err) {
      console.error('Failed to fetch current deployment:', err);
      setCurrentDeployment(null);
    }
  };

  // Every store the employee is mapped to, and whether their role allows several. Any
  // failure leaves multiStoreAllowed false — i.e. the modal behaves exactly as it always has.
  const fetchStoreAssignments = async (empCode: string) => {
    try {
      const response = await storesAPI.getAssignments(empCode, selectedProjectId ?? undefined);
      const data = response.data?.data;
      setStoreAssignments(Array.isArray(data?.stores) ? data.stores : []);
      setMultiStoreAllowed(data?.multiStoreAllowed === true);
    } catch (err) {
      console.error('Failed to fetch store assignments:', err);
      setStoreAssignments([]);
      setMultiStoreAllowed(false);
    }
  };

  const handleOpenDeployModal = (employee: Employee) => {
    setDeployModalEmployee(employee);
    setStoreSearch('');
    setSelectedStoreId('');
    setCurrentDeployment(null);
    setStoreAssignments([]);
    setMultiStoreAllowed(false);
    setIsDeployModalOpen(true);
    fetchAvailableStores(); // Fetch stores when modal opens
    // Fetch current deployment for the employee
    const empCode = getEmployeeCode(employee);
    if (empCode) {
      fetchCurrentDeployment(empCode);
      fetchStoreAssignments(empCode);
    }
  };

  const handleCloseDeployModal = () => {
    setIsDeployModalOpen(false);
    setDeployModalEmployee(null);
    setStoreSearch('');
    setSelectedStoreId('');
    setAvailableStores([]);
    setCurrentDeployment(null);
    setStoreAssignments([]);
    setMultiStoreAllowed(false);
  };

  const filteredStoreOptions = useMemo(() => {
    const query = storeSearch.trim().toLowerCase();
    
    // Filter out the store where the employee is currently deployed — and, for a
    // multi-store employee, every store they're already mapped to.
    const currentDeploymentId = currentDeployment?.id || currentDeployment?.store_id;
    let stores = availableStores;
    if (currentDeploymentId) {
      stores = availableStores.filter((store) => store.id !== currentDeploymentId);
    }
    if (multiStoreAllowed && storeAssignments.length > 0) {
      const alreadyMapped = new Set(storeAssignments.map((s) => String(s.id)));
      stores = stores.filter((store) => !alreadyMapped.has(String(store.id)));
    }
    
    if (!query) return stores;
    return stores.filter((store) =>
      [store.name, store.city, store.address, store.pinCode, store.category]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(query))
    );
  }, [storeSearch, availableStores, currentDeployment, multiStoreAllowed, storeAssignments]);

  const handleRedeployEmployee = async () => {
    if (!selectedStoreId || !deployModalEmployee) return;
    
    const employeeCode = getEmployeeCode(deployModalEmployee);
    if (!employeeCode) {
      alert('Employee code missing');
      return;
    }
    
    // Find the selected store
    const selectedStore = availableStores.find(s => s.id === selectedStoreId);
    if (!selectedStore?.id) {
      alert('Store ID is missing');
      return;
    }
    // The store this move takes them out of, as the window showed it, for the hiring question below.
    const leftId = String(currentDeployment?.id || currentDeployment?.store_id || '');
    const leftName = String(currentDeployment?.store_name || currentDeployment?.storeName || '');
    const personName = getName(deployModalEmployee);

    // Hiring at the store they join is asked about before anything is saved (product owner,
    // 22 Oct 2026); with nothing to ask the deploy goes on at once. The store they leave is asked
    // about once it is saved: never offered for hiring when it was on Outside Approved – Redeploy
    // before this move (the server clears the mark as the last person leaves, so only the store as
    // the window held it can tell).
    setAssignmentBusy(true);
    const hiring = await deployHiring.before({
      joined: [{ storeId: selectedStore.id, storeName: selectedStore.name, personName }],
      left: leftId && leftId !== selectedStore.id
        ? {
          storeId: leftId,
          storeName: leftName,
          personName,
          personCode: employeeCode,
          redeployPending: currentDeployment?.redeploy_pending === true,
        }
        : null,
    });
    if (!hiring.deploy) {
      // "Don’t deploy": nothing is saved, and the deploy window is as it was.
      setAssignmentBusy(false);
      return;
    }

    let saved = false;
    try {
      // Deploy employee to selected store using storeId (stores.id)
      await storesAPI.deploy({
        storeId: selectedStore.id,
        employee_code: employeeCode,
        employee_name: personName,
        designation: deployModalEmployee.designation || '',
        date_of_joining: deployModalEmployee.dateOfJoining || deployModalEmployee.date_of_joining || '',
        salary: deployModalEmployee.fixedCtcMonthly || deployModalEmployee.ctcMonthly || 0,
        store_name: selectedStore.name,
        city: selectedStore.city,
        state: selectedStore.state,
        pin_code: selectedStore.pinCode,
        address: selectedStore.address,
        trade: selectedStore.trade || 'WC',
        store_category: selectedStore.categoryId,
      });
      saved = true;
      
      handleCloseDeployModal();
      // Refresh data first, then show success message
      await fetchEmployees();
      alert('Employee deployed successfully!');
    } catch (error) {
      console.error('Failed to deploy employee:', error);
      alert('Failed to deploy employee. Please try again.');
    } finally {
      setAssignmentBusy(false);
      // Saved: what was chosen for the hiring is done now, after the message. Not saved: nothing is done to hiring.
      if (saved) hiring.saved();
      else hiring.failed();
    }
  };

  // Multi-store: ADD the selected store, keeping every store the employee is already on.
  // The modal stays open so several stores can be added in one go — the "Mapped Stores"
  // list refreshing is the confirmation.
  const handleAddStore = async () => {
    if (!selectedStoreId || !deployModalEmployee) return;
    const employeeCode = getEmployeeCode(deployModalEmployee);
    if (!employeeCode) {
      alert('Employee code missing');
      return;
    }
    setAssignmentBusy(true);
    const addedStoreId = selectedStoreId;
    const added = availableStores.find((store) => store.id === addedStoreId);
    // Hiring at the store added is asked about before anything is saved. They keep their other stores, so none is left.
    const hiring = await deployHiring.before({
      joined: [{ storeId: addedStoreId, storeName: added?.name || '', personName: getName(deployModalEmployee) }],
    });
    if (!hiring.deploy) {
      setAssignmentBusy(false);
      return;
    }
    let saved = false;
    try {
      await storesAPI.addEmployeeStore(employeeCode, {
        storeId: addedStoreId,
        employee_name: getName(deployModalEmployee),
        designation: deployModalEmployee.designation || '',
        date_of_joining: deployModalEmployee.dateOfJoining || deployModalEmployee.date_of_joining || '',
        salary: deployModalEmployee.fixedCtcMonthly || deployModalEmployee.ctcMonthly || 0,
      });
      saved = true;
      setSelectedStoreId('');
      await Promise.all([fetchStoreAssignments(employeeCode), fetchCurrentDeployment(employeeCode)]);
      fetchEmployees();
    } catch (error: any) {
      console.error('Failed to add store:', error);
      alert(error?.response?.data?.message || 'Failed to add store. Please try again.');
    } finally {
      setAssignmentBusy(false);
      if (saved) hiring.saved();
      else hiring.failed();
    }
  };

  // Multi-store: remove the employee from ONE store; their other stores are untouched.
  const handleRemoveStore = async (store: StoreAssignment) => {
    if (!deployModalEmployee) return;
    const employeeCode = getEmployeeCode(deployModalEmployee);
    if (!employeeCode) return;
    const isLast = storeAssignments.length <= 1;
    const ok = window.confirm(
      `Remove ${getName(deployModalEmployee)} from ${store.store_name}?` +
        (isLast ? '\n\nThis is their only store, so they will no longer be deployed anywhere.' : '')
    );
    if (!ok) return;
    setAssignmentBusy(true);
    try {
      await storesAPI.undeploy(String(store.id), employeeCode);
      await Promise.all([fetchStoreAssignments(employeeCode), fetchCurrentDeployment(employeeCode)]);
      fetchEmployees();
    } catch (error: any) {
      console.error('Failed to remove store:', error);
      alert(error?.response?.data?.message || 'Failed to remove store. Please try again.');
    } finally {
      setAssignmentBusy(false);
    }
  };

  const handleSaveEmployee = async () => {
    if (!selectedEmployee) return;

    const employeeCode = getEmployeeCode(selectedEmployee);
    if (!employeeCode) {
      setSaveError('Employee code missing. Please try again.');
      return;
    }

    try {
      setIsSaving(true);
      setSaveError('');
      const promises: Promise<any>[] = [];

      // Custom field values (Seed Score is one of these now). Skipped when the
      // project has no fields, so an all-empty save is a no-op rather than a 400.
      if (selectedProjectId && customFieldDefs.length > 0) {
        promises.push(employeeCustomFieldsAPI.saveValues(selectedProjectId, employeeCode, customFieldValues));
      }

      // Reporting hierarchy — unchanged. Saved alongside the custom fields. Not for an
      // inactive employee: the modal does not offer it, so nothing here was edited.
      const category = selectedEmployee.roleCategory;
      if (selectedProjectId && category && selectedEmployee.isActiveEmployee !== false) {
        if (category === 'L1') {
          // Send unconditionally — empty array clears the mapping.
          promises.push(employeesAPI.updateManager(employeeCode, { managerCodes: selectedManagers, projectId: selectedProjectId }));
        } else if (LEVEL_BELOW[category]) {
          promises.push(employeesAPI.updateReportees(employeeCode, { reporteeCodes: selectedReportees, projectId: selectedProjectId }));
        }
      }

      await Promise.all(promises);
      // Reflect the saved values in the roster so the CSV and a re-open show them
      // without a refetch.
      updateEmployeeEverywhere(employeeCode, (emp) => ({ ...emp, custom_fields: { ...customFieldValues } }));
      handleCloseModal();
    } catch (err) {
      console.error('Failed to save', err);
      setSaveError((err as any)?.response?.data?.message || 'Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate age on network from date of joining (like reference project). `until`
  // measures to another day instead of today — an inactive employee's exit date.
  const calculateAgeOnNetwork = (dateOfJoining: string | undefined | null, until?: string | null): string => {
    if (!dateOfJoining) return '—';

    try {
      const joiningDate = new Date(dateOfJoining);
      if (isNaN(joiningDate.getTime())) return '—';

      const currentDate = until ? new Date(until) : new Date();
      if (isNaN(currentDate.getTime())) return '—';
      
      let years = currentDate.getFullYear() - joiningDate.getFullYear();
      let months = currentDate.getMonth() - joiningDate.getMonth();
      let days = currentDate.getDate() - joiningDate.getDate();
      
      // Adjust for negative days
      if (days < 0) {
        months--;
        const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
        days += lastMonth.getDate();
      }
      
      // Adjust for negative months
      if (months < 0) {
        years--;
        months += 12;
      }
      
      if (years > 0) {
        let result = `${years}y`;
        if (months > 0) result += ` ${months}m`;
        return result;
      } else if (months > 0) {
        let result = `${months}m`;
        if (days > 0) result += ` ${days}d`;
        return result;
      } else {
        return `${days}d`;
      }
    } catch {
      return '—';
    }
  };

  // Filter employees based on search criteria (data is already filtered by deployed/undeployed from API)
  // Declared here (rather than beside clearFilters further down) because the badge counts
  // below need it, and a `const` used before its declaration is a TDZ crash, not a hoist.
  const hasFiltersApplied = Object.values(filters).some((value) => value) || !!searchQuery;

  // What a link asked the search box to start with (see the tab choice further down).
  // Kept from the first render: the shell page takes q out of the address once it is
  // read, and the prop is empty from then on. Declared here because the search below
  // reads it.
  const linkSearch = useRef(initialSearch).current;
  // The search box also looks in the project's custom field values, except while it
  // still holds what a link put there: a link names a person by code, and "contains"
  // on, say, a DS Person ID could land it on somebody else. Without the definitions
  // (still loading, or failed) it is name and code only, as it always was.
  const searchedFields = linkSearch !== '' && searchQuery === linkSearch ? NO_SEARCHED_FIELDS : customFieldDefs;
  const searchText = searchQuery.trim().toLowerCase();
  /** How the search box finds this employee (null: it does not). The filter and the hint under the name both read it. */
  const searchHit = useCallback(
    (emp: Employee) => searchHitOf(emp, searchText, searchedFields),
    [searchText, searchedFields],
  );

  /**
   * The row predicate, lifted out of `filteredEmployees` so the SAME rules can be applied
   * to the inactive tab's roster for its badge. Extracted verbatim — no rule changed.
   */
  const matchesFilters = useCallback(
    (emp: Employee) => {
      const matchesSearch = searchHit(emp) !== null;
      const empBudget = ((emp as any).budgetName || (emp as any).budget_name || (emp as any).department || '').toString().toLowerCase();
      const empProject = ((emp as any).projectName || (emp as any).project_name || (emp as any).project || '').toString().toLowerCase();
      const matchesBudget = filters.budget ? empBudget === filters.budget.toLowerCase() : true;
      const matchesProject = filters.projectName ? empProject === filters.projectName.toLowerCase() : true;
      // `location` is the full job-address string, never a state, so it was never a
      // usable fallback — matching against it emptied the table for every option in
      // the old hardcoded dropdown. Now that the roster carries a real state, match
      // on that alone.
      const matchesState = filters.state ? (emp.state || '').toLowerCase() === filters.state : true;
      // Compared against the same value the City column renders (see cityOf), so
      // selecting a city shows exactly the rows displaying it.
      const matchesCity = filters.city ? cityOf(emp).toLowerCase() === filters.city : true;
      // tenure filter placeholder until backend supports it
      const matchesTenure = filters.tenure ? true : true;
      const empReportingHead = (
        (emp as any).reportingToName ||
        (emp as any).reportingManager ||
        (emp as any).reporting_manager ||
        ''
      ).toString().toLowerCase();
      const matchesReportingHead = filters.reportingHead
        ? empReportingHead === filters.reportingHead.toLowerCase()
        : true;

      return matchesSearch && matchesBudget && matchesProject && matchesState && matchesCity && matchesTenure && matchesReportingHead;
    },
    [filters, searchHit],
  );

  const filteredEmployees = useMemo(() => employees.filter(matchesFilters), [employees, matchesFilters]);

  // Column sorting — the same mechanism every report table uses. A first click sorts
  // A→Z (dates and Age on Network: newest / longest first), a second reverses it, a
  // third restores the roster's default order. One sort state serves all three tabs, so
  // a column picked on Deployed stays picked on Undeployed and Inactive. A column the
  // tab on screen does not have (Exit Date on Deployed, Official Email on Inactive) is
  // not in that tab's accessor map, which leaves the tab in its default order.
  const employeeSortAccessors = useMemo(() => buildEmployeeSortAccessors(isInactiveTab), [isInactiveTab]);
  const employeeSort = useTableSort(filteredEmployees, employeeSortAccessors, { firstDir: employeeFirstSortDir });
  // What the table lists and the CSV exports, in the order on screen.
  const sortedEmployees = employeeSort.sorted;

  /** How many rows each tab would show under the same filters — its badge value. */
  const deployedFilteredCount = useMemo(
    () => deployedEmployees.filter(matchesFilters).length,
    [deployedEmployees, matchesFilters],
  );
  const undeployedFilteredCount = useMemo(
    () => undeployedEmployees.filter(matchesFilters).length,
    [undeployedEmployees, matchesFilters],
  );
  const inactiveFilteredCount = useMemo(
    () => inactiveEmployees.filter(matchesFilters).length,
    [inactiveEmployees, matchesFilters],
  );

  // A link that names a person (initialSearch) opens on the tab that person is on. The
  // page starts on Deployed; someone not posted at a store, a manager for one, is under
  // Undeployed, and the link would otherwise land on "No employees found" with the
  // person one tab away. Once only, when the roster first arrives and the box still
  // holds what the link asked for. Without a link nothing here runs. (linkSearch, what
  // the link asked for, is declared above the search.)
  const linkTabChosen = useRef(linkSearch === '');
  useEffect(() => {
    if (linkTabChosen.current || isLoading) return;
    linkTabChosen.current = true;
    if (searchQuery === linkSearch && activeTab === 'deployed' && deployedFilteredCount === 0 && undeployedFilteredCount > 0) {
      setActiveTab('undeployed');
    }
  }, [isLoading, searchQuery, linkSearch, activeTab, deployedFilteredCount, undeployedFilteredCount, setActiveTab]);

  /**
   * Badge counts. With no filter on they stay the server totals (unchanged behaviour);
   * with a filter on they become what each tab would actually list, so a badge can never
   * contradict the "Showing X of Y" line or the rows under it.
   */
  const deployedBadge = hasFiltersApplied ? deployedFilteredCount : deployedCount;
  const undeployedBadge = hasFiltersApplied ? undeployedFilteredCount : undeployedCount;
  // Unknown until the Inactive tab has been opened for this project: no badge, rather
  // than a 0 that reads as "nobody has left".
  const inactiveLoaded = !!selectedProjectId && inactiveLoadedFor === selectedProjectId;
  const inactiveBadge: number | null = inactiveLoaded
    ? (hasFiltersApplied ? inactiveFilteredCount : inactiveEmployees.length)
    : null;

  // The table's own state follows the tab on screen: the Inactive tab has its request.
  const tableError = isInactiveTab ? inactiveError : error;
  const tableLoading = isInactiveTab ? (inactiveLoading || (!!selectedProjectId && !inactiveLoaded)) : isLoading;

  // Report the roster size up to the tab shell so it can show "Employees (N)". Uses the
  // badge values, so the heading narrows with the filters instead of sitting at the
  // project total while both tabs underneath it show a filtered number.
  useEffect(() => {
    onCount?.(deployedBadge + undeployedBadge);
  }, [deployedBadge, undeployedBadge, onCount]);

  // The same three numbers the tabs show, for the shell that draws the tabs in its row.
  // A layout effect, so the shell's tabs are redrawn before the screen is painted: the
  // numbers in the strip and the rows under it never show apart, not even for a frame.
  const tabCounts = useMemo<RosterCounts>(
    () => ({ deployed: deployedBadge, undeployed: undeployedBadge, inactive: inactiveBadge }),
    [deployedBadge, undeployedBadge, inactiveBadge],
  );
  useLayoutEffect(() => {
    onTabCounts?.(tabCounts);
  }, [tabCounts, onTabCounts]);

  const filterOptions = {
    tenure: ['All Employees', '< 6 months', '6-12 months', '> 1 year'],
  } as const;

  // Was a hardcoded four-state list that matched no real data. Derived from the
  // loaded roster like distinctBudgets/distinctProjects, so the dropdown offers
  // exactly the states the current project actually has people in.
  const distinctStates = useMemo(() => {
    const set = new Set<string>();
    employees.forEach((e) => {
      const s = e.state;
      if (s) set.add(String(s).trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [employees]);

  // Was a hardcoded four-city list unrelated to the loaded project. Built from
  // cityOf so every option is a city some row actually displays — and so selecting
  // one can never come back empty.
  const distinctCities = useMemo(() => {
    const set = new Set<string>();
    employees.forEach((e) => {
      const c = cityOf(e);
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [employees]);

  const distinctBudgets = useMemo(() => {
    const set = new Set<string>();
    employees.forEach((e) => {
      const b = (e as any).budgetName || (e as any).budget_name || (e as any).department;
      if (b) set.add(String(b));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [employees]);

  const distinctProjects = useMemo(() => {
    const set = new Set<string>();
    employees.forEach((e) => {
      const p = (e as any).projectName || (e as any).project_name || (e as any).project;
      if (p) set.add(String(p));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [employees]);

  const distinctReportingHeads = useMemo(() => {
    const set = new Set<string>();
    employees.forEach((e) => {
      const head = (e as any).reportingToName || (e as any).reportingManager || (e as any).reporting_manager;
      if (head) set.add(String(head).replace(/\s+/g, ' ').trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [employees]);

  const moreFiltersActiveCount = [filters.projectName, filters.state, filters.city, filters.tenure, filters.reportingHead].filter(Boolean).length;

  const clearFilters = () => {
    setFilters({ budget: '', projectName: '', state: '', city: '', tenure: '', reportingHead: '' });
    setSearchQuery('');
  };

  // Export the currently-visible employees as CSV — same columns and the same
  // per-field resolution the table uses, honouring the active tab (deployed /
  // undeployed), search and filters. '—' placeholders become blank cells.
  const handleDownloadEmployees = () => {
    // Drop the UI's em-dash placeholder and flatten newlines. Quotes are left alone:
    // buildCsv already wraps and escapes every cell, so doubling them here too would
    // double-escape.
    const csvCell = (v: unknown): string => {
      const s = String(v ?? '');
      return (s === '—' ? '' : s).replace(/[\r\n]+/g, ' ');
    };
    // The Inactive tab's export adds when and from where each person left. The other
    // two tabs' files are unchanged.
    const headers = [
      'Employee Code', 'Employee Name', 'Gender', 'Mobile Number', 'Role',
      // 'Designation' is the employee's budget name ("iPro" / "Senior iPro"), the same
      // value as the on-screen Designation column (designationOf) and 'Budget Name' below.
      // It is NOT the 1Office HR job title (e.g. "In-store Sales Promoter for Apple"), which
      // was removed from this export: it is neither the ChannelPlex operating role nor the
      // payroll budget line, and was identical for essentially every row. Matches the Stores
      // export, which dropped its own (differently-sourced, deploy-time-frozen) Designation
      // column for the same reason. 'Budget Name' stays where it was, so a sheet that reads
      // it by position or name keeps working.
      'Designation',
      'Reporting Head', 'Reporting Head Code', 'Client Reporting Manager',
      'Joined Date',
      ...(isInactiveTab
        ? ['Exit Date', 'Exit Date Based On', 'Last Working Day', 'Inactive Date', 'Resignation Date',
          'Last Store Code', 'Last Store Name']
        : []),
      'Official Email', 'Appointment Type', 'Employee Category', 'Budget Name', 'Project Name',
      // 'Fixed CTC (Monthly)' and 'Gratuity Component' REMOVED — compensation is highly
      // sensitive PII and must not leave the app in a bulk CSV export. (The on-screen
      // table already omits both.)
      'State', 'City', isInactiveTab ? 'Age on Network at Exit' : 'Age on Network',
      'Job Address', 'Pincode',
      // Employee custom fields (Seed Score now lives here) — one column per active field.
      ...customFieldDefs.map((f) => f.label),
    ];
    const rows = sortedEmployees.map((employee) => {
      return [
        pickField(employee, ['emp_code', 'employeeCode', 'userName'], ''),
        getName(employee),
        genderOf(employee),
        pickField(employee, ['contact_number', 'contactNo', 'mobile', 'mobile_number', 'phone'], ''),
        employee.roleName ?? '',
        designationOf(employee),
        employee.reportingToName || employee.reportingManager || employee.reporting_manager || '',
        // The manager's EMPLOYEE code. Not `reportingTo` — that is a 1Office userId.
        employee.reportingToEmpCode ?? '',
        employee.clientReportingManagers ?? '',
        pickField(employee, ['dateOfJoining', 'joinedDate', 'joiningDate', 'joined_on', 'date_of_joining'], ''),
        ...(isInactiveTab
          ? [
            employee.exitDate ?? '',
            EXIT_DATE_SOURCE_LABEL[employee.exitDateSource as string] ?? '',
            employee.lastWorkingDay ?? '',
            employee.inactiveDate ?? '',
            employee.dateOfResignation ?? '',
            employee.lastStoreCode ?? '',
            employee.lastStoreName ?? '',
          ]
          : []),
        pickField(employee, ['officialEmail', 'email', 'personalEmail'], ''),
        pickField(employee, ['appointmentType', 'employmentType', 'employment_type'], ''),
        pickField(employee, ['employeeCategory', 'category', 'employee_category'], ''),
        pickField(employee, ['budgetName', 'department', 'budget_name'], ''),
        pickField(employee, ['projectName', 'project', 'project_name'], ''),
        pickField(employee, ['stateName', 'state'], ''),
        cityOf(employee),
        calculateAgeOnNetwork(
          employee.dateOfJoining || employee.joinedDate || employee.date_of_joining,
          isInactiveTab ? employee.exitDate : undefined,
        ),
        pickField(employee, ['jobAddress', 'address', 'location_address'], ''),
        pickField(employee, ['jobPincode', 'pincode', 'pin'], ''),
        ...customFieldDefs.map((f) => formatCustomFieldForCsv(f, (employee.custom_fields as Record<string, unknown> | undefined)?.[f.id])),
      ].map(csvCell);
    });
    const date = new Date().toISOString().slice(0, 10);
    downloadCsv(`employees_${activeTab}_${date}.csv`, buildCsv(headers, rows));
  };

  // Bulk Upload + Download. When embedded in the Employee master tab shell these
  // portal into the tab row; "Custom Fields" is no longer a button here — it is a
  // sibling tab of the shell.
  const headerActions = (
    <>
      <button
        type="button"
        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={() => setIsBulkUploadOpen(true)}
        disabled={!selectedProjectId}
        title="Bulk upload custom field values for employees"
      >
        <Upload className="h-4 w-4" />
        Bulk Upload
      </button>
      <button
        type="button"
        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={handleDownloadEmployees}
        disabled={filteredEmployees.length === 0}
        title="Download the visible employees as CSV"
      >
        <Download className="h-4 w-4" />
        Download
      </button>
    </>
  );

  // Under the name, when the search found this row by a custom field value only — the
  // field and the value, so a row whose name does not match says why it is listed.
  const searchHintFor = (employee: Employee) => {
    const hit = searchHit(employee);
    if (hit?.by !== 'field') return null;
    const text = `${hit.label}: ${hit.value}`;
    return <div className="truncate text-xs text-gray-400" title={text}>{text}</div>;
  };

  const renderEmployeeMaster = () => (
    <div className="space-y-6">
      {/* Embedded, the shell owns the heading and the actions sit in its tab row. A portal
          puts nothing here, so it adds no gap of its own. */}
      {embedded && actionSlot && createPortal(headerActions, actionSlot)}

      {/* Drawn only when it holds something: inside the shell, which draws the tabs, it is
          empty except on the Inactive tab, and an empty block would still open a gap. */}
      {(!embedded || drawsOwnTabs || isInactiveTab) && (
        <div className="space-y-6">
          {!embedded && (
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-2xl font-bold text-gray-900">Employees</h2>
              </div>
              <div className="flex flex-wrap items-center gap-2">{headerActions}</div>
            </div>
          )}

          {/* The three lists, in the master-page strip. Inside EmployeeMasterPage the shell
              draws these same tabs in its own row, beside Custom Fields, and not here. */}
          {drawsOwnTabs && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <MasterTabStrip tabs={rosterTabs(tabCounts)} active={activeTab} onSelect={setActiveTab} />
            </div>
          )}

          {/* What the Inactive tab is — and is not — listing, in terms of real dates. */}
          {isInactiveTab && (
            <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div className="space-y-1">
                <p>
                  Employees marked inactive in 1Office whose exit date falls in the last 3 months
                  {inactiveMeta && (
                    <> — from <strong>{formatIsoDay(inactiveMeta.since)}</strong> to today</>
                  )}
                  . The exit date is the earlier of their inactive date and last working day.
                </p>
                <p className="text-blue-800">
                  Their custom field values are kept after they leave: open an employee to see or correct
                  them, or use Download for everyone listed.
                </p>
                {inactiveMeta && inactiveMeta.undatedCount > 0 && (
                  <p className="text-blue-800">
                    {inactiveMeta.undatedCount.toLocaleString()} other inactive employee
                    {inactiveMeta.undatedCount === 1 ? '' : 's'} cannot be listed: 1Office has no exit date
                    recorded for them.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-6">
        <section className="rounded-lg border border-gray-100 bg-white p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, code or custom field..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <select
              value={filters.budget}
              onChange={(e) => setFilters((prev) => ({ ...prev, budget: e.target.value }))}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 max-w-[220px]"
            >
              <option value="">All Budgets</option>
              {distinctBudgets.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>

            <MoreFiltersDropdown
              activeCount={moreFiltersActiveCount}
              open={showMoreFilters}
              onToggle={() => setShowMoreFilters((p) => !p)}
              onClose={() => setShowMoreFilters(false)}
            >
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Project Name</label>
                      <select
                        value={filters.projectName}
                        onChange={(e) => setFilters((prev) => ({ ...prev, projectName: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">All Projects</option>
                        {distinctProjects.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
                      <select
                        value={filters.state}
                        onChange={(e) => setFilters((prev) => ({ ...prev, state: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">All States</option>
                        {/* Value lowercased to match how matchesState compares; the
                            label keeps the roster's own casing. */}
                        {distinctStates.map((s) => (
                          <option key={s} value={s.toLowerCase()}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
                      <select
                        value={filters.city}
                        onChange={(e) => setFilters((prev) => ({ ...prev, city: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">All Cities</option>
                        {/* Value lowercased to match how matchesCity compares; the
                            label keeps the roster's own casing. */}
                        {distinctCities.map((c) => (
                          <option key={c} value={c.toLowerCase()}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Age on Network</label>
                      <select
                        value={filters.tenure}
                        onChange={(e) => setFilters((prev) => ({ ...prev, tenure: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        {filterOptions.tenure.map((option) => (
                          <option key={option} value={option === 'All Employees' ? '' : option.toLowerCase()}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Reporting Head</label>
                      <select
                        value={filters.reportingHead}
                        onChange={(e) => setFilters((prev) => ({ ...prev, reportingHead: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">All Reporting Heads</option>
                        {distinctReportingHeads.map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                    </div>
            </MoreFiltersDropdown>

            {hasFiltersApplied && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-sm text-gray-600 hover:text-gray-900 underline"
              >
                Clear all
              </button>
            )}

            <div className="ml-auto text-xs text-gray-500 whitespace-nowrap">
              Showing {filteredEmployees.length} of {employees.length}
            </div>
          </div>

          {hasFiltersApplied && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {searchQuery && (
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 border border-blue-200">
                  Search: <span className="font-semibold">{searchQuery}</span>
                  <button type="button" onClick={() => setSearchQuery('')} className="ml-1 text-blue-600 hover:text-blue-800">×</button>
                </span>
              )}
              {filters.budget && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 border border-amber-200">
                  Budget: <span className="font-semibold">{filters.budget}</span>
                  <button type="button" onClick={() => setFilters((prev) => ({ ...prev, budget: '' }))} className="ml-1 text-amber-600 hover:text-amber-800">×</button>
                </span>
              )}
              {filters.projectName && (
                <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2.5 py-1 text-xs font-medium text-purple-700 border border-purple-200">
                  Project: <span className="font-semibold">{filters.projectName}</span>
                  <button type="button" onClick={() => setFilters((prev) => ({ ...prev, projectName: '' }))} className="ml-1 text-purple-600 hover:text-purple-800">×</button>
                </span>
              )}
              {filters.state && (
                <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 border border-indigo-200">
                  State: <span className="font-semibold">{filters.state.replace(/\b\w/g, (l) => l.toUpperCase())}</span>
                  <button type="button" onClick={() => setFilters((prev) => ({ ...prev, state: '' }))} className="ml-1 text-indigo-600 hover:text-indigo-800">×</button>
                </span>
              )}
              {filters.city && (
                <span className="inline-flex items-center gap-1 rounded-full bg-cyan-50 px-2.5 py-1 text-xs font-medium text-cyan-700 border border-cyan-200">
                  City: <span className="font-semibold">{filters.city.replace(/\b\w/g, (l) => l.toUpperCase())}</span>
                  <button type="button" onClick={() => setFilters((prev) => ({ ...prev, city: '' }))} className="ml-1 text-cyan-600 hover:text-cyan-800">×</button>
                </span>
              )}
              {filters.tenure && (
                <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2.5 py-1 text-xs font-medium text-teal-700 border border-teal-200">
                  Age on Network: <span className="font-semibold">{filters.tenure}</span>
                  <button type="button" onClick={() => setFilters((prev) => ({ ...prev, tenure: '' }))} className="ml-1 text-teal-600 hover:text-teal-800">×</button>
                </span>
              )}
              {filters.reportingHead && (
                <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700 border border-rose-200">
                  Reporting Head: <span className="font-semibold">{filters.reportingHead}</span>
                  <button type="button" onClick={() => setFilters((prev) => ({ ...prev, reportingHead: '' }))} className="ml-1 text-rose-600 hover:text-rose-800">×</button>
                </span>
              )}
            </div>
          )}
        </section>

        <div className="overflow-hidden rounded-lg border border-gray-100 bg-white min-h-[400px]">
          {tableError ? (
            <div className="flex h-full min-h-[400px] flex-col items-center justify-center gap-3">
              <p className="text-sm text-red-600">{tableError}</p>
              <button
                onClick={isInactiveTab ? () => { void fetchInactiveEmployees(); } : fetchEmployees}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
              >
                Retry
              </button>
            </div>
          ) : tableLoading ? (
            <div className="h-full overflow-hidden">
              <EmployeeTableSkeleton />
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="flex min-h-[400px] flex-col items-center justify-center gap-2 py-12">
              <p className="text-lg font-medium text-gray-500">No employees found</p>
              <p className="text-sm text-gray-400">
                {activeTab === 'deployed'
                  ? 'No deployed employees available.'
                  : activeTab === 'undeployed'
                    ? 'No undeployed employees available.'
                    : employees.length > 0
                      ? 'No inactive employees match these filters.'
                      : 'Nobody on this project was marked inactive in the last 3 months.'}
              </p>
            </div>
          ) : isInactiveTab ? (
          <div className="overflow-x-auto">
              <table className="min-w-full table-fixed divide-y divide-gray-200 text-sm">
                <colgroup>
                  <col className="w-28" />
                  <col className="w-40" />
                  <col className="w-24" />
                  <col className="w-32" />
                  <col className="w-28" />
                  <col className="w-32" />
                  <col className="w-40" />
                  <col className="w-32" />
                  <col className="w-28" />
                  <col className="w-32" />
                  <col className="w-44" />
                  <col className="w-56" />
                  <col className="w-32" />
                  <col className="w-32" />
                  <col className="w-28" />
                </colgroup>
                <thead className="sticky top-0 z-20 bg-gray-100 text-left text-xs font-semibold text-gray-500">
                  <tr>
                    <SortTh label="Employee Code" sortKey="code" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Employee Name" sortKey="name" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Gender" sortKey="gender" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Mobile Number" sortKey="mobile" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Role" sortKey="role" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Designation" sortKey="designation" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Reporting Head" sortKey="reportingHead" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Manager Code" sortKey="managerCode" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Joined Date" sortKey="joinedDate" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Exit Date" sortKey="exitDate" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Age on Network at Exit" sortKey="ageOnNetwork" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Last Store" sortKey="lastStore" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="State" sortKey="state" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="City" sortKey="city" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <th className="sticky right-0 z-30 whitespace-nowrap bg-gray-100 px-4 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white text-left">
                  {sortedEmployees.map((employee, index) => {
                    const code = pickField(employee, ['emp_code', 'employeeCode', 'userName'], `emp-${index}`);
                    const dateOfJoining = employee.dateOfJoining || employee.joinedDate || employee.date_of_joining;
                    const exitSource = EXIT_DATE_SOURCE_LABEL[employee.exitDateSource as string];
                    const mobileNumber = pickField(employee, ['contact_number', 'contactNo', 'mobile', 'mobile_number', 'phone']);
                    return (
                      <tr key={`${code}-${index}`} className="group hover:bg-gray-50">
                        <td className="whitespace-nowrap px-4 py-4 text-left font-medium text-gray-900">{code}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-left text-gray-900">
                          {getName(employee)}
                          {searchHintFor(employee)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{genderOf(employee) || '—'}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{mobileNumber || '—'}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{employee.roleName || '—'}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{designationOf(employee) || '—'}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{employee.reportingToName || employee.reportingManager || employee.reporting_manager || '—'}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{employee.reportingToEmpCode || '—'}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{pickField(employee, ['dateOfJoining', 'joinedDate', 'joiningDate', 'joined_on', 'date_of_joining'])}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">
                          <div>{employee.exitDate || '—'}</div>
                          {exitSource && <div className="text-xs text-gray-400">{exitSource}</div>}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{calculateAgeOnNetwork(dateOfJoining, employee.exitDate)}</td>
                        <td className="px-4 py-4 text-left text-gray-600">
                          {employee.lastStoreName ? (
                            <div className="min-w-0">
                              <div className="truncate text-gray-900" title={employee.lastStoreName}>{employee.lastStoreName}</div>
                              <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-400">
                                {employee.lastStoreCode && <span>{employee.lastStoreCode}</span>}
                                {employee.stillMappedToStore && (
                                  <span
                                    className="rounded-full bg-amber-50 px-1.5 py-0.5 font-medium text-amber-700 ring-1 ring-amber-200"
                                    title="Marked inactive in 1Office, but still mapped to this store in ChannelPlex."
                                  >
                                    Still mapped
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{pickField(employee, ['stateName', 'state'])}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{cityOf(employee) || '—'}</td>
                        <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-4 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] group-hover:bg-gray-50">
                          {/* No Deploy action: somebody who has left cannot be deployed. */}
                          <TableActionGroup>
                            <TableActionButton action="view" label="View employee details" type="button" onClick={() => {
                                const uid = employee.userId ?? employee.user_id;
                                if (uid) setDetailsModalUserId(uid);
                                else alert('User id is not available for this employee.');
                              }} />
                            <TableActionButton action="edit" label="Edit custom fields" type="button" onClick={() => handleEdit(employee)} />
                          </TableActionGroup>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
          </div>
          ) : (
          <div className="overflow-x-auto">
              <table className="min-w-full table-fixed divide-y divide-gray-200 text-sm">
                <colgroup>
                  <col className="w-28" />
                  <col className="w-32" />
                  <col className="w-24" />
                  <col className="w-32" />
                  <col className="w-28" />
                  <col className="w-32" />
                  <col className="w-20" />
                  <col className="w-36" />
                  <col className="w-44" />
                  <col className="w-24" />
                  <col className="w-56" />
                  <col className="w-32" />
                  <col className="w-32" />
                  <col className="w-36" />
                  <col className="w-36" />
                  <col className="w-28" />
                  <col className="w-32" />
                  <col className="w-36" />
                  <col className="w-32" />
                  <col className="w-32" />
                  <col className="w-32" />
                  <col className="w-48" />
                  <col className="w-24" />
                  <col className="w-24" />
                </colgroup>
                <thead className="sticky top-0 z-20 bg-gray-100 text-left text-xs font-semibold text-gray-500">
                  <tr>
                    <SortTh label="Employee Code" sortKey="code" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Employee Name" sortKey="name" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Gender" sortKey="gender" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Mobile Number" sortKey="mobile" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Role" sortKey="role" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Designation" sortKey="designation" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Reporting Head" sortKey="reportingHead" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Manager Code" sortKey="managerCode" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Client Reporting Manager" sortKey="clientReportingManager" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Joined Date" sortKey="joinedDate" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Official Email" sortKey="officialEmail" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    {/* Appointment Type / Employee Category / Budget Name / Project
                        Name / HR Designation / Fixed CTC / Gratuity Component / Job
                        Address were dropped from the grid on request — the first
                        five are constant for everyone on a project (see the header
                        above the table), and pay + address are sensitive detail that
                        does not belong in a shared on-screen roster. All are still
                        in the row's detail view, and all but the HR Designation
                        (dropped from the export earlier, see buildCsv below) in the
                        CSV. The Designation column above is the budget name
                        ("iPro" / "Senior iPro"), not that HR field. */}
                    <SortTh label="State" sortKey="state" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="City" sortKey="city" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Age on Network" sortKey="ageOnNetwork" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <SortTh label="Pincode" sortKey="pincode" sort={employeeSort} className="whitespace-nowrap px-4 py-4" />
                    <th className="sticky right-0 z-30 whitespace-nowrap bg-gray-100 px-4 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white text-left">
                  {
                    sortedEmployees.map((employee, index) => {
                      const code = pickField(
                        employee,
                        ['emp_code', 'employeeCode', 'userName'],
                        `emp-${index}`
                      );
                      const joinedDate = pickField(employee, ['dateOfJoining', 'joinedDate', 'joiningDate', 'joined_on', 'date_of_joining']);
                      const officialEmail = pickField(employee, ['officialEmail', 'email', 'personalEmail']);
                      const city = cityOf(employee) || '—';
                      // Sourced server-side from the store the employee is currently
                      // deployed to — the 1Office roster itself carries no state — so
                      // this is blank for the Undeployed tab.
                      const state = pickField(employee, ['stateName', 'state']);
                      // Calculate age on network from date of joining
                      const dateOfJoining = employee.dateOfJoining || employee.joinedDate || employee.date_of_joining;
                      const ageOnNetwork = calculateAgeOnNetwork(dateOfJoining);
                      const pincode = pickField(employee, ['jobPincode', 'pincode', 'pin']);
                      const mobileNumber = pickField(employee, ['contact_number', 'contactNo', 'mobile', 'mobile_number', 'phone']);
                      return (
                        <tr key={`${code}-${index}`} className="group hover:bg-gray-50">
                          <td className="whitespace-nowrap px-4 py-4 text-left font-medium text-gray-900">{code}</td>
                          <td className="whitespace-nowrap px-4 py-4 text-left text-gray-900">
                            {getName(employee)}
                            {searchHintFor(employee)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{genderOf(employee) || '—'}</td>
                          <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{mobileNumber || '—'}</td>
                          <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{employee.roleName || '—'}</td>
                          <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{designationOf(employee) || '—'}</td>
                          <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{employee.reportingToName || employee.reportingManager || employee.reporting_manager || '—'}</td>
                          <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{employee.reportingToEmpCode || '—'}</td>
                          <td className="px-4 py-4 text-left text-gray-600" title={employee.clientReportingManagers || ''}>{employee.clientReportingManagers || '—'}</td>
                          <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{joinedDate}</td>
                          <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{officialEmail}</td>
                          <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{state}</td>
                          <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{city}</td>
                          <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{ageOnNetwork}</td>
                          <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{pincode}</td>
                          <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-4 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] group-hover:bg-gray-50">
                            <TableActionGroup>
                              <TableActionButton action="view" label="View employee details" type="button" onClick={() => {
                                  const uid = employee.userId ?? employee.user_id;
                                  if (uid) setDetailsModalUserId(uid);
                                  else alert('User id is not available for this employee.');
                                }} />
                              <TableActionButton action="edit" label="Edit employee" type="button" onClick={() => handleEdit(employee)} />
                              <TableActionButton action="assign" label="Deploy employee" type="button" onClick={() => handleOpenDeployModal(employee)} />
                            </TableActionGroup>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
          </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="space-y-6">
        {renderEmployeeMaster()}
      </div>

      <Modal
        isOpen={isEditModalOpen && Boolean(selectedEmployee)}
        onClose={handleCloseModal}
        title="Employee Details"
        size="md"
        headerContent={
          <div className="flex items-center justify-between rounded-t-lg bg-primary-600 px-6 py-4">
            <h3 className="text-lg font-semibold text-white">Employee Details</h3>
            <button
              onClick={handleCloseModal}
              className="rounded-full p-1 text-primary-100 transition hover:text-white"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        }
        footer={
          <>
            <button
              type="button"
              onClick={handleCloseModal}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveEmployee}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-70"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      >
        {selectedEmployee && (() => {
          const empCategory = selectedEmployee.roleCategory;
          // Somebody who has left keeps their custom fields (correctable here) but has no
          // reporting line left to manage, so those sections are not offered.
          const isInactiveEmployee = selectedEmployee.isActiveEmployee === false;
          const levelBelow = empCategory && !isInactiveEmployee ? LEVEL_BELOW[empCategory] : null;
          // Get employees from the level below for multi-select (L2→L1s, L3→L2s, etc.)
          const reporteeOptions = levelBelow
            ? employees.filter((e) => e.roleCategory === levelBelow)
            : [];

          return (
            <div className="space-y-5 text-sm text-gray-700">
              <div className="rounded-xl bg-gray-50 px-4 py-3">
                <p className="text-xs font-medium uppercase text-gray-500">Employee</p>
                <p className="text-base font-semibold text-gray-900">{getName(selectedEmployee)}</p>
                <p className="mt-1 text-sm text-gray-500">
                  Employee Code: <span className="font-semibold text-gray-800">{getEmployeeCode(selectedEmployee) || '—'}</span>
                </p>
                {selectedEmployee.roleName && (
                  <p className="mt-1 text-sm text-gray-500">
                    Role: <span className="font-semibold text-gray-800">{selectedEmployee.roleName}</span>
                    {empCategory && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700">
                        {empCategory}
                      </span>
                    )}
                  </p>
                )}
                {isInactiveEmployee && (
                  <p className="mt-1 text-sm text-gray-500">
                    Inactive since:{' '}
                    <span className="font-semibold text-gray-800">
                      {selectedEmployee.exitDate ? formatIsoDay(String(selectedEmployee.exitDate)) : '—'}
                    </span>
                    <span className="ml-2 inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700">
                      Inactive
                    </span>
                  </p>
                )}
              </div>

              {/* Project custom fields (Seed Score is one of these for projects that
                  define it). The renderer is shared with the store form. */}
              {customFieldDefs.length > 0 && (
                <StoreCustomFieldInputs
                  fields={customFieldDefs}
                  values={customFieldValues}
                  onChange={handleCustomFieldChange}
                />
              )}

              {saveError && <p className="text-xs text-red-600">{saveError}</p>}

              {/* Current Reporting Head from system */}
              {selectedEmployee && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Current Reporting Head
                  </label>
                  <p className="w-full rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-800">
                    {selectedEmployee.reportingToName || selectedEmployee.reportingManager || selectedEmployee.reporting_manager || '—'}
                  </p>
                </div>
              )}

              {/* Client Reporting Managers — external users (multi-select) */}
              {!isInactiveEmployee && externalUsers.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Client Reporting Managers
                  </label>
                  <p className="text-xs text-gray-500">
                    Select one or more external users this employee should report to.
                  </p>
                  {loadingReporting ? (
                    <InlineLoader label="Loading..." size="xs" className="flex" />
                  ) : (
                    <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                      {externalUsers.map((ext) => {
                        const isChecked = selectedManagers.includes(ext.employee_code);
                        return (
                          <label
                            key={ext.employee_code}
                            className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                setSelectedManagers((prev) =>
                                  isChecked
                                    ? prev.filter((c) => c !== ext.employee_code)
                                    : [...prev, ext.employee_code]
                                );
                              }}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-800">
                              {ext.name}{' '}
                              <span className="text-xs text-gray-400">({ext.employee_code})</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {!loadingReporting && selectedManagers.length > 0 && (
                    <p className="text-xs text-gray-500">
                      {selectedManagers.length} manager{selectedManagers.length === 1 ? '' : 's'} selected
                    </p>
                  )}
                </div>
              )}

              {/* Reportees — for L2-L5 employees */}
              {levelBelow && reporteeOptions.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Reporting Employees ({levelBelow})
                  </label>
                  {loadingReporting ? (
                    <InlineLoader label="Loading..." size="xs" className="flex" />
                  ) : (
                    <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                      {reporteeOptions.map((emp) => {
                        const code = getEmployeeCode(emp);
                        const isChecked = selectedReportees.includes(code);
                        return (
                          <label
                            key={code}
                            className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                setSelectedReportees((prev) =>
                                  isChecked ? prev.filter((c) => c !== code) : [...prev, code]
                                );
                              }}
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-800">
                              {getName(emp)} <span className="text-gray-400">({code})</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-xs text-gray-400">
                    {selectedReportees.length} of {reporteeOptions.length} selected
                  </p>
                </div>
              )}
            </div>
          );
        })()}
      </Modal>

      <Modal
        isOpen={isDeployModalOpen && Boolean(deployModalEmployee)}
        onClose={handleCloseDeployModal}
        title="Deploy Employee to Store"
        size="xl"
        headerContent={
          <div className="flex items-center justify-between rounded-t-lg bg-primary-600 px-6 py-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-100">Deployment</p>
              <h3 className="text-lg font-semibold text-white">Deploy Employee to Store</h3>
              {deployModalEmployee && (
                <p className="text-xs text-primary-100">
                  {getName(deployModalEmployee)} · Code {getEmployeeCode(deployModalEmployee)}
                </p>
              )}
            </div>
            <button
              onClick={handleCloseDeployModal}
              className="rounded-full p-2 text-primary-100 transition hover:text-white"
              aria-label="Close deploy employee modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        }
        footer={
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={handleCloseDeployModal}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={multiStoreAllowed ? handleAddStore : handleRedeployEmployee}
              disabled={!selectedStoreId || assignmentBusy}
              // text-white belongs to the enabled branch only: left on the base it also sat
              // on the disabled button beside text-gray-500, and Tailwind emits text-white
              // later, so the label stayed white on the grey button.
              className={`rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-colors ${selectedStoreId && !assignmentBusy ? 'bg-primary-600 text-white hover:bg-primary-700' : 'cursor-not-allowed bg-gray-300 text-gray-500'}`}
            >
              {multiStoreAllowed ? 'Add Store' : 'Redeploy to Store'}
            </button>
          </div>
        }
      >
        {deployModalEmployee && (
          <div className="grid max-h-[70vh] gap-6 lg:grid-cols-3">
            <div className="flex flex-col overflow-hidden rounded-lg border border-gray-100 bg-gray-50 p-4 lg:col-span-2">
              <div className="mb-4">
                <p className="text-sm font-semibold text-gray-900">
                  {multiStoreAllowed ? 'Add a Store' : 'Select New Store for Deployment'}
                </p>
                <p className="text-xs text-gray-500">
                  {multiStoreAllowed
                    ? 'Their role allows several stores — adding one keeps the stores they are already on.'
                    : 'Search by store name, city, or trade.'}
                </p>
              </div>
              <div className="mb-4">
                <label className="sr-only" htmlFor="store-search">Search store</label>
                <input
                  id="store-search"
                  type="text"
                  value={storeSearch}
                  onChange={(event) => setStoreSearch(event.target.value)}
                  placeholder="Search by store name, city, or trade..."
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                />
              </div>

              <div className="grid flex-1 content-start gap-3 overflow-y-auto sm:grid-cols-2">
                {filteredStoreOptions.length === 0 ? (
                  <p className="col-span-2 rounded-xl border border-dashed border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-500">
                    No stores match your search.
                  </p>
                ) : (
                  filteredStoreOptions.map((store) => {
                    const isSelected = selectedStoreId === store.id;
                    return (
                      <button
                        key={store.id}
                        type="button"
                        onClick={() => setSelectedStoreId(store.id)}
                        className={`text-left rounded-2xl border px-4 py-3 shadow-sm transition-all ${
                          isSelected
                            ? 'border-primary-300 bg-white ring-2 ring-primary-200'
                            : 'border-gray-200 bg-white hover:border-primary-200'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{store.name}</p>
                            <p className="text-xs text-gray-500">Store Category: {store.category ?? '—'}</p>
                          </div>
                          {isSelected && (
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-white">
                              ✓
                            </span>
                          )}
                        </div>
                        <dl className="mt-3 space-y-1 text-xs text-gray-600">
                          <div className="flex justify-between">
                            <dt className="font-medium text-gray-500">PIN Code:</dt>
                            <dd className="text-primary-600">{store.pinCode}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt>City:</dt>
                            <dd>{store.city}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt>Address:</dt>
                            <dd className="truncate text-right">{store.address}</dd>
                          </div>
                        </dl>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <aside className="flex flex-col space-y-4 self-start rounded-lg border border-gray-100 bg-gray-50 p-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-900">Employee Information</h4>
                <dl className="mt-3 space-y-2 text-sm">
                  <div className="flex justify-between gap-2">
                    <dt className="text-gray-500 shrink-0">Employee Name:</dt>
                    <dd className="font-medium text-gray-900 text-right">{getName(deployModalEmployee)}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-gray-500 shrink-0">Employee Code:</dt>
                    <dd className="text-gray-700 text-right">{getEmployeeCode(deployModalEmployee) || '—'}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-gray-500 shrink-0">Date of Joining:</dt>
                    <dd className="text-gray-700 text-right">
                      {(() => {
                        const dateValue = pickField(deployModalEmployee, ['joinedDate', 'joiningDate', 'joined_on', 'date_of_joining'], '');
                        if (!dateValue || dateValue === '—') return '—';
                        const date = new Date(dateValue);
                        if (isNaN(date.getTime())) return dateValue;
                        return date.toLocaleDateString('en-CA') + ' ' + date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                      })()}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-gray-500 shrink-0">Fixed CTC (Monthly):</dt>
                    <dd className="text-gray-700 text-right">{pickField(deployModalEmployee, ['fixedCtcMonthly', 'ctcMonthly', 'fixed_ctc'], '—')}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-gray-500 shrink-0">Designation:</dt>
                    <dd className="text-gray-700 text-right">{pickField(deployModalEmployee, ['designation'], '—')}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-gray-500 shrink-0">City Name:</dt>
                    <dd className="text-gray-700 text-right">{pickField(deployModalEmployee, ['city', 'location'], '—')}</dd>
                  </div>
                </dl>
              </div>

              {multiStoreAllowed ? (
                <div className="rounded-lg border border-gray-100 bg-white p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-primary-700">
                    <span className="inline-flex h-2.5 w-2.5 rounded-full bg-primary-500" />
                    Mapped Stores ({storeAssignments.length})
                  </div>
                  {storeAssignments.length === 0 ? (
                    <p className="mt-3 text-sm text-gray-500">Not mapped to any store yet.</p>
                  ) : (
                    <ul className="mt-2 divide-y divide-gray-100">
                      {storeAssignments.map((s) => (
                        <li key={s.id} className="flex items-start justify-between gap-2 py-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-gray-900">{s.store_name}</p>
                            <p className="truncate text-xs text-gray-500">
                              {[s.store_code, s.city].filter(Boolean).join(' · ') || '—'}
                              {s.is_primary ? ' · Primary' : ''}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveStore(s)}
                            disabled={assignmentBusy}
                            className="shrink-0 rounded p-1 text-gray-400 transition hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                            aria-label={`Remove from ${s.store_name}`}
                            title="Remove from this store"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border border-gray-100 bg-white p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-primary-700">
                    <span className="inline-flex h-2.5 w-2.5 rounded-full bg-primary-500" />
                    Current Deployment
                  </div>
                  <div className="mt-3 space-y-2 text-sm text-gray-600">
                    <div>
                      <p className="text-xs font-semibold uppercase text-gray-500">Deployed At:</p>
                      <p className="text-base font-semibold text-primary-700">
                        {currentDeployment?.store_name || currentDeployment?.storeName || 'Not Assigned'}
                      </p>
                    </div>
                    {currentDeployment && (
                      <div className="flex items-center gap-2 text-xs font-semibold text-green-600">
                        <span className="inline-flex h-2 w-2 rounded-full bg-green-500" />
                        {currentDeployment.store_status || 'Active'}
                      </div>
                    )}
                    <dl className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">City:</dt>
                        <dd className="text-gray-700">{currentDeployment?.city || currentDeployment?.store_city || '—'}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">State:</dt>
                        <dd className="text-gray-700">{currentDeployment?.state || currentDeployment?.store_state || '—'}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Address:</dt>
                        <dd className="text-gray-700 truncate max-w-[120px]">{currentDeployment?.address || currentDeployment?.store_address || '—'}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Pincode:</dt>
                        <dd className="text-gray-700">{currentDeployment?.pin_code || currentDeployment?.pinCode || currentDeployment?.store_pin_code || '—'}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
              )}
            </aside>
          </div>
        )}
      </Modal>

      <EmployeeDetailsModal
        isOpen={detailsModalUserId !== null}
        onClose={() => setDetailsModalUserId(null)}
        userId={detailsModalUserId}
      />

      {selectedProjectId && (
        <EmployeeCustomFieldsBulkUploadModal
          isOpen={isBulkUploadOpen}
          onClose={() => setIsBulkUploadOpen(false)}
          onSuccess={refreshAfterBulkUpload}
          projectId={selectedProjectId}
        />
      )}

      {/* The hiring question when a deploy is pressed: over the deploy window, before anything is saved. */}
      {deployHiring.window}
    </>
  );
}
